"use client";

import React from "react";
import styles from '../../../styles/modules/cookie-consent.module.scss';

const CONSENT_COOKIE_NAME = 'zaraz-consent-preferences';
const EU_COOKIE_NAME = 'cf-is-eu';

// Fired by the footer "Cookie preferences" link to bring the banner back.
export const OPEN_COOKIE_PREFERENCES_EVENT = 'openCookiePreferences';

interface ZarazPurpose {
    id: string;
    name: string;
    description: string;
    order: number;
}

interface ConsentState {
    hasConsented: boolean | null;
    purposes: Record<string, ZarazPurpose>;
    selectedPurposes: Record<string, boolean>;
    isEU: boolean | null;
}

// Helper to get cookie value
const getCookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
};

// Helper to check if user is from EU
const checkIsEU = (): boolean | null => {
    const euCookie = getCookie(EU_COOKIE_NAME);
    if (euCookie === null) return null; // Cookie not set yet
    return euCookie === '1';
};

// Dispatch custom event when consent status changes
const dispatchConsentEvent = (hasConsent: boolean, isEU: boolean) => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('cookieConsentUpdated', {
            detail: { hasConsent, isEU }
        }));
    }
};

export const CookieConsentBanner = (): React.ReactElement | null => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [showDetails, setShowDetails] = React.useState(false);
    const [consentState, setConsentState] = React.useState<ConsentState>({
        hasConsented: null,
        purposes: {},
        selectedPurposes: {},
        isEU: null,
    });

    const checkExistingConsent = React.useCallback(() => {
        const existingConsent = getCookie(CONSENT_COOKIE_NAME);
        return existingConsent !== null;
    }, []);

    const initializeConsent = React.useCallback(() => {
        const isEU = checkIsEU();
        const hasExistingConsent = checkExistingConsent();

        // If not from EU, auto-grant consent and don't show banner
        if (isEU === false) {
            // Auto-grant consent for non-EU users
            if (window.zaraz?.consent) {
                window.zaraz.consent.setAll(true);
                window.zaraz.consent.sendQueuedEvents();
            }

            // Set cookie to remember auto-consent
            if (!hasExistingConsent) {
                document.cookie = `${CONSENT_COOKIE_NAME}=auto-granted; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
            }

            setConsentState(prev => ({
                ...prev,
                hasConsented: true,
                isEU: false,
            }));

            // Dispatch event for tracking components
            dispatchConsentEvent(true, false);
            return;
        }

        // EU user - check if they've already consented
        if (isEU === true) {
            // Get purposes from Zaraz if available
            const purposes = window.zaraz?.consent?.purposes || {};
            const currentConsent = window.zaraz?.consent?.getAll() || {};

            const hasZarazConsent = Object.keys(currentConsent).length > 0 &&
                Object.values(currentConsent).some(v => v !== undefined);

            setConsentState({
                hasConsented: hasExistingConsent || hasZarazConsent,
                purposes,
                selectedPurposes: currentConsent,
                isEU: true,
            });

            // Show banner only if EU user hasn't consented yet
            if (!hasExistingConsent && !hasZarazConsent) {
                setIsVisible(true);
            } else {
                // Dispatch event with current consent status
                const consentValue = getCookie(CONSENT_COOKIE_NAME);
                const hasConsent = consentValue === 'all' || consentValue === 'auto-granted' ||
                    (consentValue !== null && consentValue !== 'none');
                dispatchConsentEvent(hasConsent, true);
            }
        }
    }, [checkExistingConsent]);

    React.useEffect(() => {
        // Wait a bit for the EU cookie to be set by Cloudflare middleware
        const checkAndInit = () => {
            const isEU = checkIsEU();

            // If EU status is determined, initialize
            if (isEU !== null) {
                initializeConsent();
                return;
            }

            // If cookie not set yet, wait and retry (max 3 attempts)
            let attempts = 0;
            const interval = setInterval(() => {
                attempts++;
                const euStatus = checkIsEU();
                if (euStatus !== null || attempts >= 3) {
                    clearInterval(interval);
                    initializeConsent();
                }
            }, 500);

            return () => clearInterval(interval);
        };

        // Listen for Zaraz API ready event
        const handleZarazReady = () => {
            checkAndInit();
        };

        if (window.zaraz?.consent?.APIReady) {
            checkAndInit();
        } else {
            document.addEventListener('zarazConsentAPIReady', handleZarazReady);
            // Also initialize without Zaraz after a timeout
            const timeout = setTimeout(() => {
                checkAndInit();
            }, 2000);

            return () => {
                document.removeEventListener('zarazConsentAPIReady', handleZarazReady);
                clearTimeout(timeout);
            };
        }
    }, [initializeConsent]);

    // Reopen on demand so visitors can change an earlier choice.
    React.useEffect(() => {
        const handleOpen = () => {
            const purposes = window.zaraz?.consent?.purposes || {};
            const currentConsent = window.zaraz?.consent?.getAll() || {};
            setConsentState(prev => ({
                ...prev,
                purposes,
                selectedPurposes: currentConsent,
            }));
            setShowDetails(false);
            setIsVisible(true);
        };

        window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, handleOpen);
        return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, handleOpen);
    }, []);

    const handleAcceptAll = React.useCallback(() => {
        if (window.zaraz?.consent) {
            window.zaraz.consent.setAll(true);
            window.zaraz.consent.sendQueuedEvents();
        }

        document.cookie = `${CONSENT_COOKIE_NAME}=all; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;

        setConsentState(prev => ({ ...prev, hasConsented: true }));
        setIsVisible(false);

        dispatchConsentEvent(true, true);
    }, []);

    const handleRejectAll = React.useCallback(() => {
        if (window.zaraz?.consent) {
            window.zaraz.consent.setAll(false);
        }

        document.cookie = `${CONSENT_COOKIE_NAME}=none; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;

        setConsentState(prev => ({ ...prev, hasConsented: true }));
        setIsVisible(false);

        dispatchConsentEvent(false, true);
    }, []);

    const handleSavePreferences = React.useCallback(() => {
        if (window.zaraz?.consent) {
            window.zaraz.consent.set(consentState.selectedPurposes);

            if (Object.values(consentState.selectedPurposes).some(v => v)) {
                window.zaraz.consent.sendQueuedEvents();
            }
        }

        const prefsJson = JSON.stringify(consentState.selectedPurposes);
        document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(prefsJson)}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;

        setConsentState(prev => ({ ...prev, hasConsented: true }));
        setIsVisible(false);
        setShowDetails(false);

        const hasAnyConsent = Object.values(consentState.selectedPurposes).some(v => v);
        dispatchConsentEvent(hasAnyConsent, true);
    }, [consentState.selectedPurposes]);

    const handleTogglePurpose = React.useCallback((purposeId: string) => {
        setConsentState(prev => ({
            ...prev,
            selectedPurposes: {
                ...prev.selectedPurposes,
                [purposeId]: !prev.selectedPurposes[purposeId],
            },
        }));
    }, []);

    const handleManagePreferences = React.useCallback(() => {
        setShowDetails(true);
    }, []);

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    const purposesList = Object.entries(consentState.purposes);

    return (
        <div className={styles.cookie_consent__overlay}>
            <div className={styles.cookie_consent__banner} role="dialog" aria-modal="true" aria-labelledby="cookie-consent-title">
                {!showDetails ? (
                    <>
                        <div className={styles.cookie_consent__content}>
                            <h2 id="cookie-consent-title" className={styles.cookie_consent__title}>
                                We value your privacy
                            </h2>
                            <p className={styles.cookie_consent__description}>
                                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
                                By clicking &quot;Accept All&quot;, you consent to our use of cookies.
                            </p>
                        </div>
                        <div className={styles.cookie_consent__actions}>
                            <button
                                type="button"
                                className={styles.cookie_consent__button_secondary}
                                onClick={handleRejectAll}
                            >
                                Reject All
                            </button>
                            <button
                                type="button"
                                className={styles.cookie_consent__button_secondary}
                                onClick={handleManagePreferences}
                            >
                                Manage Preferences
                            </button>
                            <button
                                type="button"
                                className={styles.cookie_consent__button_primary}
                                onClick={handleAcceptAll}
                            >
                                Accept All
                            </button>
                        </div>
                        <div className={styles.cookie_consent__links}>
                            <a href="/policy/privacy-policy" className={styles.cookie_consent__link}>
                                Privacy Policy
                            </a>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.cookie_consent__content}>
                            <h2 id="cookie-consent-title" className={styles.cookie_consent__title}>
                                Cookie Preferences
                            </h2>
                            <p className={styles.cookie_consent__description}>
                                Manage your cookie preferences below. Essential cookies are always enabled as they are required for the website to function properly.
                            </p>
                        </div>
                        <div className={styles.cookie_consent__purposes}>
                            <div className={styles.cookie_consent__purpose}>
                                <div className={styles.cookie_consent__purpose_header}>
                                    <span className={styles.cookie_consent__purpose_name}>Essential Cookies</span>
                                    <span className={styles.cookie_consent__purpose_required}>Always Active</span>
                                </div>
                                <p className={styles.cookie_consent__purpose_description}>
                                    These cookies are necessary for the website to function and cannot be disabled.
                                </p>
                            </div>
                            {purposesList.map(([id, purpose]) => (
                                <div key={id} className={styles.cookie_consent__purpose}>
                                    <div className={styles.cookie_consent__purpose_header}>
                                        <label className={styles.cookie_consent__purpose_label}>
                                            <input
                                                type="checkbox"
                                                checked={consentState.selectedPurposes[id] || false}
                                                onChange={() => handleTogglePurpose(id)}
                                                className={styles.cookie_consent__checkbox}
                                            />
                                            <span className={styles.cookie_consent__purpose_name}>{purpose.name}</span>
                                        </label>
                                    </div>
                                    <p className={styles.cookie_consent__purpose_description}>
                                        {purpose.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <div className={styles.cookie_consent__actions}>
                            <button
                                type="button"
                                className={styles.cookie_consent__button_secondary}
                                onClick={() => setShowDetails(false)}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className={styles.cookie_consent__button_primary}
                                onClick={handleSavePreferences}
                            >
                                Save Preferences
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CookieConsentBanner;
